import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ICollectionStrategy } from '../../interfaces/collection-strategy.interface';
import { IDaysRange } from '../../interfaces/channel-config.interface';
import { ScenarioEnum } from '../../enum/scenario.enum';
import { DrippingValidators } from '../../validators/dripping.validators';
import { DAYS_RANGE_LABELS, FREQUENCY_CONFIG } from '../../constants/strategies.constants';

/**
 * Servicio para gestionar el estado del wizard de estrategias
 * Maneja el FormGroup, navegación entre pasos y validaciones
 * 
 * ACTUALIZADO: Wizard de 2 pasos con daysRange en lugar de daysOffset
 */
@Injectable({
  providedIn: 'root'
})
export class StrategyService {
  entity!: FormGroup;
  currentStep = 1;
  maxSteps = 2; // Reducido de 3 a 2 pasos
  isEditMode = false;
  selectedStrategy: ICollectionStrategy | null = null;

  constructor(private fb: FormBuilder) {}

  /**
   * Inicializa un nuevo formulario vacío para crear estrategia
   */
  newEntity(): void {
    this.entity = this.fb.group({
      // Paso 1: Información Básica + Configuración Temporal + Ejecución
      name: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', [Validators.maxLength(500)]],
      masterTemplateId: ['', [Validators.required]],
      scenario: ['POST_DUE_DATE', [Validators.required]],
      
      // Rango de días (reemplaza daysOffset)
      // Si maxDays es null, se considera sin límite máximo
      daysRange: this.fb.group({
        minDays: [1, [Validators.required, Validators.min(0)]],
        maxDays: [30, [Validators.min(0)]]
      }),
      
      // Switches de ejecución (movidos desde paso 3)
      autoExecute: [true],
      active: [true],
      
      // Paso 2: Configuración de Canales
      channels: this.fb.group({
        email: this.fb.group({
          enabled: [true, [Validators.required]],
          templateId: [''],
          priority: [1, [Validators.required, Validators.min(1), Validators.max(4)]],
          scheduledTime: ['09:00', [Validators.pattern(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/)]],
          frequency: [1, [Validators.min(1), Validators.max(10)]],
          frequencyInterval: [FREQUENCY_CONFIG.DEFAULT_INTERVAL, [Validators.min(FREQUENCY_CONFIG.MIN_INTERVAL)]]
        }),
        sms: this.fb.group({
          enabled: [false, [Validators.required]],
          templateId: [''],
          priority: [2, [Validators.required, Validators.min(1), Validators.max(4)]],
          scheduledTime: [''],
          frequency: [1, [Validators.min(1), Validators.max(10)]],
          frequencyInterval: [FREQUENCY_CONFIG.DEFAULT_INTERVAL, [Validators.min(FREQUENCY_CONFIG.MIN_INTERVAL)]]
        }),
        whatsapp: this.fb.group({
          enabled: [false, [Validators.required]],
          templateId: [''],
          priority: [3, [Validators.required, Validators.min(1), Validators.max(4)]],
          scheduledTime: [''],
          frequency: [1, [Validators.min(1), Validators.max(10)]],
          frequencyInterval: [FREQUENCY_CONFIG.DEFAULT_INTERVAL, [Validators.min(FREQUENCY_CONFIG.MIN_INTERVAL)]]
        }),
        call: this.fb.group({
          enabled: [false, [Validators.required]],
          templateId: [''],
          priority: [4, [Validators.required, Validators.min(1), Validators.max(4)]],
          scheduledTime: [''],
          frequency: [1, [Validators.min(1), Validators.max(10)]],
          frequencyInterval: [FREQUENCY_CONFIG.DEFAULT_INTERVAL, [Validators.min(FREQUENCY_CONFIG.MIN_INTERVAL)]]
        })
      }, {
        validators: [
          DrippingValidators.atLeastOneChannelEnabled(),
          DrippingValidators.uniquePriorities(),
          DrippingValidators.allEnabledChannelsHaveScheduledTime()
        ]
      })
    });

    this.currentStep = 1;
    this.isEditMode = false;
    this.selectedStrategy = null;
  }

  /**
   * Carga una estrategia existente en el formulario para editar
   */
  loadEntityForEdit(strategy: any): void {
    this.selectedStrategy = strategy;
    this.isEditMode = true;
    
    // Inicializar el formulario primero si no existe
    if (!this.entity) {
      this.newEntity();
    }
    
    // Extraer masterTemplateId (puede venir como string o como objeto populated)
    const masterTemplateId = typeof strategy.masterTemplateId === 'string' 
      ? strategy.masterTemplateId 
      : strategy.masterTemplateId?._id || '';
    
    // Mapear daysRange del backend (null = sin límite)
    const daysRange = strategy.daysRange || { minDays: 0, maxDays: null };
    
    // Mapear channels con frequency
    const channelsData = strategy.channels || {};
    const mappedChannels = {
      email: this.mapChannelData(channelsData.email),
      sms: this.mapChannelData(channelsData.sms),
      whatsapp: this.mapChannelData(channelsData.whatsapp),
      call: this.mapChannelData(channelsData.call)
    };
    
    this.entity.patchValue({
      name: strategy.name,
      description: strategy.description || '',
      masterTemplateId: masterTemplateId,
      scenario: strategy.scenario,
      daysRange: {
        minDays: daysRange.minDays,
        maxDays: daysRange.maxDays // null = sin límite
      },
      autoExecute: strategy.autoExecute ?? true,
      active: strategy.active ?? true,
      channels: mappedChannels
    });

    this.currentStep = 1;
  }

  /**
   * Mapea datos de un canal desde el backend
   */
  private mapChannelData(channel: any): any {
    if (!channel) {
      return {
        enabled: false,
        templateId: '',
        priority: 1,
        scheduledTime: '',
        frequency: 1,
        frequencyInterval: FREQUENCY_CONFIG.DEFAULT_INTERVAL
      };
    }
    
    return {
      enabled: channel.enabled || false,
      templateId: channel.templateId || '',
      priority: channel.priority || 1,
      scheduledTime: channel.scheduledTime || '',
      frequency: channel.frequency || 1,
      frequencyInterval: channel.frequencyInterval || FREQUENCY_CONFIG.DEFAULT_INTERVAL
    };
  }

  /**
   * Prepara los datos del formulario para enviar al backend
   */
  getFormDataForSubmit(): any {
    const formValue = this.entity.getRawValue();
    
    // Construir daysRange según el escenario
    let daysRange: IDaysRange;
    
    switch (formValue.scenario) {
      case ScenarioEnum.PRE_DUE_DATE:
        // PRE_DUE_DATE: Solo un día específico (recordatorio, no insistente)
        // minDays = maxDays = el día seleccionado
        daysRange = {
          minDays: formValue.daysRange.minDays,
          maxDays: formValue.daysRange.minDays // Mismo valor = solo ese día
        };
        break;
        
      case ScenarioEnum.DUE_DATE:
        // DUE_DATE: Exactamente el día del vencimiento
        daysRange = { minDays: 0, maxDays: 0 };
        break;
        
      case ScenarioEnum.POST_DUE_DATE:
      default:
        // POST_DUE_DATE: Rango de días (cobranza)
        daysRange = {
          minDays: formValue.daysRange.minDays,
          maxDays: formValue.daysRange.maxDays ?? null // null = sin límite
        };
        break;
    }
    
    // Construir channels
    const channels = {
      email: this.cleanChannelData(formValue.channels.email),
      sms: this.cleanChannelData(formValue.channels.sms),
      whatsapp: this.cleanChannelData(formValue.channels.whatsapp),
      call: this.cleanChannelData(formValue.channels.call)
    };
    
    return {
      name: formValue.name,
      description: formValue.description,
      masterTemplateId: formValue.masterTemplateId,
      scenario: formValue.scenario,
      daysRange,
      channels,
      autoExecute: formValue.autoExecute,
      active: formValue.active
    };
  }

  /**
   * Limpia datos de canal para envío
   */
  private cleanChannelData(channel: any): any {
    return {
      enabled: channel.enabled,
      templateId: channel.templateId,
      priority: channel.priority,
      scheduledTime: channel.scheduledTime,
      frequency: channel.frequency || 1,
      frequencyInterval: channel.frequency > 1 ? channel.frequencyInterval : undefined
    };
  }

  /**
   * Avanza al siguiente paso del wizard
   */
  nextStep(): void {
    if (!this.validateStep(this.currentStep)) {
      return;
    }
    
    if (this.currentStep < this.maxSteps) {
      this.currentStep++;
    }
  }

  /**
   * Retrocede al paso anterior del wizard
   */
  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  /**
   * Valida un paso específico del wizard
   */
  validateStep(step: number): boolean {
    // Protección: si el formulario no está inicializado, retornar false
    if (!this.entity) {
      return false;
    }

    switch (step) {
      case 1:
        // Validar campos básicos, temporales y daysRange
        const basicValid = this.entity.get('name')?.valid &&
                          this.entity.get('masterTemplateId')?.valid &&
                          this.entity.get('scenario')?.valid;
        
        // Validar daysRange
        const daysRangeValid = this.validateDaysRange();
        
        return !!(basicValid && daysRangeValid);
      
      case 2:
        // Validar configuración de canales
        return this.validateChannelsConfig();
      
      default:
        return true;
    }
  }

  /**
   * Valida el rango de días según el scenario
   */
  validateDaysRange(): boolean {
    const scenario = this.entity.get('scenario')?.value;
    const daysRangeForm = this.entity.get('daysRange');
    
    if (!daysRangeForm) return false;
    
    const minDays = daysRangeForm.get('minDays')?.value;
    const maxDays = daysRangeForm.get('maxDays')?.value;
    
    // DUE_DATE no requiere validación de rango
    if (scenario === ScenarioEnum.DUE_DATE) {
      return true;
    }
    
    // minDays debe ser >= 0
    if (minDays < 0) return false;
    
    // Si maxDays está definido, debe ser >= minDays (null = sin límite, válido)
    if (maxDays !== null && maxDays !== undefined && maxDays < minDays) {
      return false;
    }
    
    return true;
  }

  /**
   * Valida la configuración de canales según reglas de negocio
   */
  validateChannelsConfig(): boolean {
    const channels = this.entity.get('channels')?.value;
    
    if (!channels) {
      return false;
    }
    
    // Validación 1: Al menos 1 canal debe estar enabled
    const hasEnabledChannel = channels.email.enabled ||
                              channels.sms.enabled ||
                              channels.whatsapp.enabled ||
                              channels.call.enabled;
    
    if (!hasEnabledChannel) {
      return false;
    }
    
    // Validación 2: templateId requerido si canal está enabled
    const enabledChannels = Object.keys(channels).filter(
      (key) => channels[key].enabled
    );
    
    for (const channelKey of enabledChannels) {
      const channel = channels[channelKey];
      if (!channel.templateId || channel.templateId.trim() === '') {
        return false;
      }
      
      // Validación de frequency
      if (channel.frequency < 1) {
        return false;
      }
      
      // Si frequency > 1, frequencyInterval debe ser >= 5
      if (channel.frequency > 1 && (!channel.frequencyInterval || channel.frequencyInterval < FREQUENCY_CONFIG.MIN_INTERVAL)) {
        return false;
      }
    }
    
    // Validación 3: Validar priorities únicas
    const priorities: number[] = [];
    if (channels.email.enabled) priorities.push(channels.email.priority);
    if (channels.sms.enabled) priorities.push(channels.sms.priority);
    if (channels.whatsapp.enabled) priorities.push(channels.whatsapp.priority);
    if (channels.call.enabled) priorities.push(channels.call.priority);
    
    const uniquePriorities = new Set(priorities);
    if (priorities.length !== uniquePriorities.size) {
      return false;
    }
    
    // Validación 4: scheduledTime requerido en todos los canales habilitados
    for (const channelKey of enabledChannels) {
      const channel = channels[channelKey];
      if (!channel.scheduledTime) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Obtiene el texto descriptivo del escenario con rango de días
   */
  getScenarioDisplayText(scenario: string, daysRange?: IDaysRange): string {
    const labels = DAYS_RANGE_LABELS[scenario as keyof typeof DAYS_RANGE_LABELS];
    
    if (!labels) return scenario;
    
    if (scenario === ScenarioEnum.DUE_DATE) {
      return labels.preview(0, 0);
    }
    
    if (daysRange) {
      return labels.preview(daysRange.minDays, daysRange.maxDays);
    }
    
    return labels.title;
  }

  /**
   * Obtiene los labels dinámicos según el scenario actual
   */
  getDaysRangeLabels(): typeof DAYS_RANGE_LABELS.POST_DUE_DATE {
    const scenario = this.entity?.get('scenario')?.value || 'POST_DUE_DATE';
    return DAYS_RANGE_LABELS[scenario as keyof typeof DAYS_RANGE_LABELS] || DAYS_RANGE_LABELS.POST_DUE_DATE;
  }

  /**
   * Calcula los horarios de envío basados en frequency
   */
  calculateScheduledTimes(scheduledTime: string, frequency: number, interval: number): string[] {
    if (!scheduledTime || frequency <= 1) {
      return scheduledTime ? [scheduledTime] : [];
    }
    
    const times: string[] = [];
    const [hours, minutes] = scheduledTime.split(':').map(Number);
    let currentMinutes = hours * 60 + minutes;
    
    for (let i = 0; i < frequency; i++) {
      const h = Math.floor(currentMinutes / 60) % 24;
      const m = currentMinutes % 60;
      times.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      currentMinutes += interval;
    }
    
    return times;
  }
}
